import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Stethoscope, Filter, X, ArrowUpDown, Star, Wifi, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import axios from 'axios';
import DoctorCard from '../components/DoctorCard';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SearchPage = () => {
  const { t } = useLanguage();
  const [clinics, setClinics] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [specialty, setSpecialty] = useState(searchParams.get('specialty') || '');
  const [wilaya, setWilaya] = useState(searchParams.get('wilaya') || '');
  const [daira, setDaira] = useState(searchParams.get('daira') || '');
  const [searchName, setSearchName] = useState(searchParams.get('name') || '');
  const [onlineOnly, setOnlineOnly] = useState(searchParams.get('online') === 'true');
  const [videoOnly, setVideoOnly] = useState(searchParams.get('video') === 'true');
  // Bouton "Laboratoires" (demandé par Nadjib) : raccourci en un clic, pas un
  // filtre de plus dans le panneau — les labos n'ont pas de spécialité, donc
  // ils étaient invisibles à qui cherchait spécifiquement un laboratoire.
  const [entityType, setEntityType] = useState(searchParams.get('entity_type') || '');
  const [sortBy, setSortBy] = useState('relevance');
  const [specialties, setSpecialties] = useState([]);
  const [wilayas, setWilayas] = useState([]);
  const [dairas, setDairas] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [specRes, wilRes] = await Promise.all([
          axios.get(`${API_URL}/api/specialties`),
          axios.get(`${API_URL}/api/wilayas`)
        ]);
        setSpecialties(specRes.data);
        setWilayas(wilRes.data);
      } catch (error) {
        console.error('Error fetching reference data:', error);
      }
    };
    fetchReferenceData();
  }, []);

  useEffect(() => {
    if (!wilaya || wilaya === 'all') { setDairas([]); setDaira(''); return; }
    axios.get(`${API_URL}/api/wilayas/${wilaya}/communes`)
      .then(r => setDairas(r.data))
      .catch(() => setDairas([]));
    setDaira('');
  }, [wilaya]);

  useEffect(() => {
    const handler = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/doctors/suggest?q=${encodeURIComponent(q)}`);
      setSuggestions(res.data);
      setShowSuggestions(true);
    } catch { setSuggestions([]); }
  }, []);

  const handleNameChange = (e) => {
    const val = e.target.value;
    setSearchName(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  useEffect(() => {
    const fetchDoctors = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (specialty && specialty !== 'all') params.set('specialty', specialty);
        if (wilaya && wilaya !== 'all') params.set('wilaya', wilaya);
        if (searchName) params.set('name', searchName);
        if (onlineOnly) params.set('online_only', 'true');
        if (videoOnly) params.set('video_only', 'true');
        if (daira && daira !== 'all') params.set('daira', daira);
        if (entityType) params.set('entity_type', entityType);
        params.set('sort_by', sortBy);
        params.set('page', page.toString());
        params.set('limit', '12');

        const response = await axios.get(`${API_URL}/api/doctors?${params.toString()}`);

        // Charger aussi les cliniques avec les memes filtres. Correctif : le
        // bouton "Laboratoires" sautait entierement /api/clinics ("les
        // cliniques sont une collection à part, sans rapport avec un labo")
        // — mais un laboratoire inscrit sur la plateforme (LaboRegister.js,
        // page publique + RDV en ligne, ClinicProfile.js) EST justement une
        // clinique en base (establishment_type=laboratory), distincte des
        // fiches "Laboratoire" importées de doctofree dans /api/doctors. Sans
        // ce filtre, un vrai laboratoire avec reservation en ligne restait
        // invisible ici. Pour "Cliniques", rien ne change.
        // "Clinique" isole les fiches doctofree (specialties=Clinique) déjà
        // couvertes par /api/doctors : /api/clinics n'a rien à y ajouter.
        if (entityType === 'Clinique') {
          setClinics([]);
        } else {
          try {
            const clinicParams = new URLSearchParams();
            if (wilaya && wilaya !== 'all') clinicParams.set('wilaya', wilaya);
            if (daira && daira !== 'all') clinicParams.set('daira', daira);
            if (entityType === 'Laboratoire') {
              clinicParams.set('establishment_type', 'laboratory');
            } else if (specialty && specialty !== 'all') {
              // Convertir le code en nom (ex: 'dentistry' -> 'Dentiste')
              const specObj = specialties.find(s => s.id === specialty);
              const specName = specObj ? specObj.name_fr : specialty;
              clinicParams.set('specialty', specName);
            }
            if (searchName) clinicParams.set('name', searchName);
            const clinicsRes = await axios.get(`${API_URL}/api/clinics?${clinicParams.toString()}`);
            setClinics(clinicsRes.data.clinics || []);
          } catch (e) {
            setClinics([]);
          }
        }
        setDoctors(response.data.doctors || []);
        setTotal(response.data.total || 0);
        setPages(response.data.pages || 1);
      } catch (error) {
        console.error('Error fetching doctors:', error);
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDoctors();
  }, [specialty, wilaya, daira, searchName, onlineOnly, videoOnly, entityType, sortBy, page]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setShowSuggestions(false);
    setPage(1);
    const params = new URLSearchParams();
    if (specialty && specialty !== 'all') params.set('specialty', specialty);
    if (wilaya && wilaya !== 'all') params.set('wilaya', wilaya);
    if (daira && daira !== 'all') params.set('daira', daira);
    if (searchName) params.set('name', searchName);
    if (onlineOnly) params.set('online', 'true');
    if (videoOnly) params.set('video', 'true');
    if (entityType) params.set('entity_type', entityType);
    setSearchParams(params);
  };

  // Raccourcis "Laboratoires" / "Cliniques" (demandés par Nadjib) : ces
  // établissements importés de doctofree n'ont pas de spécialité médicale,
  // donc ils restent invisibles à qui cherche justement un labo ou une
  // clinique — ce toggle les isole via entity_type. Un seul actif à la fois
  // (re-cliquer dessus ou choisir une vraie spécialité le désactive).
  const toggleEntityFilter = (value) => {
    setEntityType((prev) => {
      const next = prev === value ? '' : value;
      // Ni un labo ni une clinique n'ont de spécialité médicale
      // (specialty=None en base) : si une spécialité restait sélectionnée
      // (ex. "Médecine générale" choisie plus tôt), la recherche combinait
      // les deux et ne trouvait jamais rien — repéré par Nadjib (0 résultat
      // malgré 225 labos).
      if (next) setSpecialty('');
      return next;
    });
    setPage(1);
  };

  const clearFilters = () => {
    setSpecialty('');
    setWilaya('');
    setDaira('');
    setSearchName('');
    setOnlineOnly(false);
    setVideoOnly(false);
    setEntityType('');
    setSortBy('relevance');
    setPage(1);
    setSearchParams({});
  };

  const activeFiltersCount = [
    specialty && specialty !== 'all',
    wilaya && wilaya !== 'all',
    daira && daira !== 'all',
    onlineOnly,
    videoOnly
  ].filter(Boolean).length;

  const FilterContent = () => (
    <div className="space-y-6">
      <div>
        <Label className="text-sm font-medium mb-2 block">{t('search_specialty')}</Label>
        <Select value={specialty} onValueChange={(v) => { setSpecialty(v); setEntityType(''); setPage(1); }}>
          <SelectTrigger data-testid="filter-specialty">
            <SelectValue placeholder={t('search_page_all_specialties')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('search_page_all_specialties')}</SelectItem>
            {specialties.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name_fr}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-sm font-medium mb-2 block">{t('search_wilaya')}</Label>
        <Select value={wilaya} onValueChange={(v) => { setWilaya(v); setPage(1); }}>
          <SelectTrigger data-testid="filter-wilaya">
            <SelectValue placeholder={t('search_page_all_wilayas')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('search_page_all_wilayas')}</SelectItem>
            {wilayas.map((w) => (
              <SelectItem key={w.code} value={w.code}>{w.code} - {w.name_fr}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-sm font-medium mb-2 block">{t('search_page_daira')}</Label>
        <Select value={daira} onValueChange={(v) => { setDaira(v); setPage(1); }} disabled={!wilaya || wilaya === 'all'}>
          <SelectTrigger data-testid="filter-daira">
            <SelectValue placeholder={t('search_page_all_dairas')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('search_page_all_dairas')}</SelectItem>
            {dairas.map((d) => (
              <SelectItem key={d.name_fr} value={d.name_fr}>{d.name_fr}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between">
        <Label htmlFor="online-only" className="text-sm font-medium flex items-center gap-2">
          <Wifi className="w-4 h-4 text-[#10B981]" />
          {t('search_page_online_only')}
        </Label>
        <Switch
          id="online-only"
          checked={onlineOnly}
          onCheckedChange={(v) => { setOnlineOnly(v); setPage(1); }}
          data-testid="online-only-switch"
        />
      </div>

      <div className="flex items-center justify-between">
        <Label htmlFor="video-only" className="text-sm font-medium flex items-center gap-2">
          <Video className="w-4 h-4 text-[#0596DE]" />
          {t('search_page_video_only')}
        </Label>
        <Switch
          id="video-only"
          checked={videoOnly}
          onCheckedChange={(v) => { setVideoOnly(v); setPage(1); }}
          data-testid="video-only-switch"
        />
      </div>

      <div>
        <Label className="text-sm font-medium mb-2 block">{t('search_page_sort')}</Label>
        <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
          <SelectTrigger data-testid="sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="relevance">{t('search_page_relevance')}</SelectItem>
            <SelectItem value="rating">{t('search_page_best_rating')}</SelectItem>
            <SelectItem value="name">{t('search_page_name_az')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button variant="outline" onClick={clearFilters} className="w-full" data-testid="clear-filters-btn">
        <X className="w-4 h-4 mr-2" />
        {t('search_page_clear')}
      </Button>
    </div>
  );

  const seoSpecialtyLabel = (specialties.find((s) => s.id === specialty) || {}).name_fr || '';
  const seoWilayaLabel = (wilayas.find((w) => w.code === wilaya) || {}).name_fr || '';
  const seoTitleParts = [];
  if (seoSpecialtyLabel) seoTitleParts.push(`${seoSpecialtyLabel}s`);
  if (seoWilayaLabel) seoTitleParts.push(`à ${seoWilayaLabel}`);
  const seoTitle = seoTitleParts.length > 0
    ? `${seoTitleParts.join(' ')} | Tabbibak`
    : 'Trouvez un médecin en Algérie | Tabbibak';
  const seoDescription = seoTitleParts.length > 0
    ? `Trouvez un ${seoSpecialtyLabel || 'médecin'}${seoWilayaLabel ? ` à ${seoWilayaLabel}` : ''} et prenez rendez-vous en ligne gratuitement sur Tabbibak.`
    : 'Recherchez un médecin par spécialité et par wilaya, et prenez rendez-vous en ligne gratuitement partout en Algérie.';
  const seoCanonicalParams = new URLSearchParams();
  if (specialty && specialty !== 'all') seoCanonicalParams.set('specialty', specialty);
  if (wilaya && wilaya !== 'all') seoCanonicalParams.set('wilaya', wilaya);
  const seoCanonical = seoCanonicalParams.toString()
    ? `https://tabbibak.com/search?${seoCanonicalParams.toString()}`
    : 'https://tabbibak.com/search';

  return (
    <>
    <Helmet>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <link rel="canonical" href={seoCanonical} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={seoCanonical} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
    </Helmet>
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      
      {/* Search Header */}
      <div className="bg-white border-b border-[#E2E8F0] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3" data-testid="search-form">
            <div className="flex-1 relative" ref={suggestRef}>
              <div className="flex items-center gap-2 bg-[#F8FAFC] rounded-full px-4 py-2">
                <Search className="w-5 h-5 text-[#64748B]" />
                <Input
                  type="text"
                  placeholder={t('search_page_placeholder')}
                  value={searchName}
                  onChange={handleNameChange}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  className="border-0 shadow-none focus-visible:ring-0 bg-transparent"
                  data-testid="search-input"
                />
              </div>
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-xl border border-[#E2E8F0] shadow-lg overflow-hidden" data-testid="suggestions-dropdown">
                  {suggestions.map((s) => (
                    <Link
                      key={s.doctor_id}
                      to={`/doctor/${s.doctor_id}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[#F8FAFC] transition-colors"
                      onClick={() => setShowSuggestions(false)}
                    >
                      {s.photo_url ? (
                        <img src={s.photo_url} alt={s.name} className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-[#E6F4FB] flex items-center justify-center">
                          <span className="text-sm font-bold text-[#0596DE]">
                            {s.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-[#0F172A] text-sm">{s.name}</p>
                        <p className="text-xs text-[#64748B]">{s.specialty}</p>
                      </div>
                      {s.is_registered && (
                        <span className="ml-auto text-xs px-2 py-0.5 bg-[#D1FAE5] text-[#10B981] rounded-full">{t('search_page_online')}</span>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Button type="submit" className="rounded-full bg-[#0596DE] hover:bg-[#047CBA]" data-testid="search-btn">
              {t('search_page_btn')}
            </Button>

            {/* Raccourcis "Laboratoires" / "Cliniques" (demandés par Nadjib) :
                ces établissements n'ont pas de spécialité, donc ils restaient
                invisibles à qui cherchait justement un labo ou une clinique —
                un clic ici les isole directement. */}
            <Button
              type="button"
              variant={entityType === 'Laboratoire' ? 'default' : 'outline'}
              onClick={() => toggleEntityFilter('Laboratoire')}
              className={`rounded-full whitespace-nowrap ${entityType === 'Laboratoire' ? 'bg-[#0596DE] hover:bg-[#047CBA]' : ''}`}
              data-testid="filter-laboratoire-btn"
            >
              🔬 Laboratoires
            </Button>
            <Button
              type="button"
              variant={entityType === 'Clinique' ? 'default' : 'outline'}
              onClick={() => toggleEntityFilter('Clinique')}
              className={`rounded-full whitespace-nowrap ${entityType === 'Clinique' ? 'bg-[#0596DE] hover:bg-[#047CBA]' : ''}`}
              data-testid="filter-clinique-btn"
            >
              🏥 Cliniques
            </Button>

            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="rounded-full md:hidden relative" data-testid="mobile-filter-btn">
                  <Filter className="w-5 h-5" />
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#0596DE] text-white text-xs rounded-full flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>{t('search_page_filters')}</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <FilterContent />
                </div>
              </SheetContent>
            </Sheet>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-4 md:gap-8">
          {/* Desktop Filters */}
          <div className="hidden md:block w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 sticky top-24">
              <h3 className="font-semibold text-[#0F172A] mb-4">{t('search_page_filters')}</h3>
              <FilterContent />
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-[#0F172A]" data-testid="results-count">
                {loading
                  ? t('search_page_searching')
                  : entityType === 'Laboratoire'
                    ? `${total} laboratoire${total > 1 ? 's' : ''} trouvé${total > 1 ? 's' : ''}`
                    : entityType === 'Clinique'
                      ? `${total} clinique${total > 1 ? 's' : ''} trouvée${total > 1 ? 's' : ''}`
                      : `${total} ${total > 1 ? t('search_page_doctors') : t('search_page_doctor')} ${total > 1 ? t('search_page_found_plural') : t('search_page_found')}`}
              </h2>
              <div className="hidden md:flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-[#64748B]" />
                <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
                  <SelectTrigger className="w-[180px] border-0 shadow-none" data-testid="sort-desktop">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">{t('search_page_relevance')}</SelectItem>
                    <SelectItem value="rating">
                      <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {t('search_page_best_rating')}</span>
                    </SelectItem>
                    <SelectItem value="name">{t('search_page_name_az')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loading ? (
              <div className="grid md:grid-cols-2 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 animate-pulse">
                    <div className="flex gap-4">
                      <div className="w-20 h-20 rounded-xl bg-gray-200" />
                      <div className="flex-1 space-y-3">
                        <div className="h-5 bg-gray-200 rounded w-3/4" />
                        <div className="h-4 bg-gray-200 rounded w-1/2" />
                        <div className="h-4 bg-gray-200 rounded w-full" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : doctors.length === 0 && clinics.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-[#E6F4FB] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Stethoscope className="w-12 h-12 text-[#0596DE]" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-2">{t('search_page_no_results')}</h3>
                <p className="text-[#64748B] mb-4">{t('search_page_try_different')}</p>
                <Button variant="outline" onClick={clearFilters}>
                  {t('search_page_clear')}
                </Button>
              </div>
            ) : (
              <>
                {clinics.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <h3 className="text-lg font-semibold text-[#0F172A]">
                        {entityType === 'Laboratoire' ? 'Laboratoires' : 'Cliniques'} ({clinics.length})
                      </h3>
                      <span className="text-xs px-2 py-0.5 bg-[#E0F2FE] text-[#0596DE] rounded-full">
                        {entityType === 'Laboratoire' ? 'Prise de RDV en ligne' : 'Plusieurs médecins'}
                      </span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {clinics.map((c) => (
                        <Link
                          key={c.clinic_id}
                          to={`/clinic/${c.slug}`}
                          className="bg-white rounded-2xl border border-[#E2E8F0] p-5 hover:shadow-md hover:border-[#0596DE] transition group"
                        >
                          <div className="flex items-start gap-4">
                            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0596DE] to-[#047CBA] flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {c.logo_url ? (
                                <img src={c.logo_url} alt={c.name} className="w-full h-full object-cover" />
                              ) : (
                                <Stethoscope className="w-8 h-8 text-white" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs px-2 py-0.5 bg-[#0596DE] text-white rounded-full font-medium">
                                  {c.establishment_type === 'laboratory' ? 'Laboratoire' : (c.clinic_type || 'Clinique')}
                                </span>
                                {c.doctor_count > 0 && (
                                  <span className="text-xs text-[#64748B]">
                                    {c.doctor_count} médecin(s)
                                  </span>
                                )}
                              </div>
                              <h4 className="font-semibold text-[#0F172A] group-hover:text-[#0596DE] transition truncate">
                                {c.name}
                              </h4>
                              <p className="text-sm text-[#64748B] mt-1 truncate">
                                {c.daira ? `${c.daira}, ` : ''}{c.address}
                              </p>
                              {c.specialties && c.specialties.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {c.specialties.slice(0, 3).map((s, i) => (
                                    <span key={i} className="text-xs px-2 py-0.5 bg-[#F1F5F9] text-[#475569] rounded-full">
                                      {s}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {doctors.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-[#0F172A]">
                      Médecins ({doctors.length})
                    </h3>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                  {doctors.map((doctor) => (
                    <DoctorCard key={doctor.doctor_id} doctor={doctor} />
                  ))}
                </div>

                {pages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button 
                      variant="outline" 
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-full"
                    >
                      {t('search_page_prev')}
                    </Button>
                    {(() => {
  const delta = 2;
  const start = Math.max(1, page - delta);
  const end = Math.min(pages, page + delta);
  return [...Array(end - start + 1)].map((_, i) => {
    const p = start + i;
    return (
      <Button
        key={p}
        variant={page === p ? 'default' : 'outline'}
        className={`rounded-full w-10 h-10 p-0 ${page === p ? 'bg-[#0596DE]' : ''}`}
        onClick={() => setPage(p)}
      >
        {p}
      </Button>
    );
  });
})()}
                    <Button 
                      variant="outline" 
                      onClick={() => setPage(p => Math.min(pages, p + 1))}
                      disabled={page === pages}
                      className="rounded-full"
                    >
                      {t('search_page_next')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default SearchPage;
